import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowDropDown, Search } from "@mui/icons-material";
import { useSelector } from "react-redux";

import { HOSPITAL_UNASSIGNED } from "static/constants";

/**
 * Hospital column filter: the hospitals assigned to this user, with a search box.
 * Offering only the assignment is what stops it reaching a hospital they cannot access.
 * In a popover because the column is too narrow for hospital names.
 */
const HospitalColumnFilter = ({ column, includeUnassigned = false }) => {
  const { currentUser } = useSelector((state) => state.auth);

  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState([]);
  const [search, setSearch] = useState("");

  const options = useMemo(() => {
    const assigned = Array.isArray(currentUser?.followingHospitals)
      ? currentUser.followingHospitals
      : [];

    const names = [
      ...new Set(
        assigned
          .map((hospital) => String(hospital ?? "").trim())
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b));

    // Opt-in, so it sits after the real hospitals.
    return includeUnassigned ? [...names, HOSPITAL_UNASSIGNED] : names;
  }, [currentUser, includeUnassigned]);

  const raw = column.getFilterValue();
  const selected = Array.isArray(raw)
    ? raw
    : String(raw ?? "")
        .split(",")
        .filter(Boolean);

  // No filter set means everything is included, so that is what the boxes show.
  const effectiveSelection = selected.length ? selected : options;

  // A count, since the column is too narrow for names. Unassigned is counted apart from
  // the hospitals, being the absence of one.
  const summary = () => {
    if (!selected.length) return "All hospitals";

    const hospitals = selected.filter(
      (hospital) => hospital !== HOSPITAL_UNASSIGNED
    ).length;
    const hasUnassigned = hospitals !== selected.length;

    if (!hospitals) return "Unassigned";

    const label = hospitals === 1 ? "1 hospital" : `${hospitals} hospitals`;

    return hasUnassigned ? `${label} + Unassigned` : label;
  };

  const visibleOptions = options.filter((hospital) =>
    hospital.toLowerCase().includes(search.trim().toLowerCase())
  );

  const handleOpen = (event) => {
    setDraft(effectiveSelection);
    setSearch("");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const toggle = (hospital, checked) =>
    setDraft((current) =>
      checked
        ? [...current, hospital]
        : current.filter((name) => name !== hospital)
    );

  const handleApply = () => {
    // Sent as it stands even when full: an unset filter matches every hospital, a full
    // selection only the ones listed here.
    column.setFilterValue(draft.length ? draft : undefined);
    handleClose();
  };

  const isDirty =
    draft.length !== effectiveSelection.length ||
    draft.some((hospital) => !effectiveSelection.includes(hospital));

  const open = Boolean(anchorEl);

  return (
    <>
      <TextField
        variant="outlined"
        margin="dense"
        fullWidth
        value={summary()}
        onClick={handleOpen}
        inputProps={{
          readOnly: true,
          style: { cursor: "pointer", textOverflow: "ellipsis" },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDown fontSize="small" style={{ color: "grey" }} />
            </InputAdornment>
          ),
        }}
        // Matches the padding the table gives its own filter inputs.
        sx={{
          "& .MuiInputBase-root": { cursor: "pointer" },
          "& .MuiInputBase-input": { py: "8px" },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: { borderRadius: "8px", width: 320, maxWidth: "90vw" },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Hospitals
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {draft.length} of {options.length} selected
          </Typography>
        </Box>
        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search hospitals"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" style={{ color: "grey" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />

        <Box sx={{ px: 2, py: 1, maxHeight: 260, overflowY: "auto" }}>
          {visibleOptions.length ? (
            visibleOptions.map((hospital) => (
              <FormControlLabel
                key={hospital}
                sx={{ m: 0, py: 0.25, display: "flex" }}
                control={
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={draft.includes(hospital)}
                    onChange={(_event, checked) => toggle(hospital, checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    {hospital === HOSPITAL_UNASSIGNED ? "Unassigned" : hospital}
                  </Typography>
                }
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No hospitals match “{search}”
            </Typography>
          )}
        </Box>
        <Divider />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" onClick={() => setDraft(options)}>
              Select all
            </Button>
            <Button size="small" onClick={() => setDraft([])}>
              Clear all
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" color="inherit" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              disabled={!isDirty}
              onClick={handleApply}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default HospitalColumnFilter;
